import * as React from 'react'
import { useQueryCompanyRoles } from '@/lib/api/company/roles'
import type { Option } from '@/ui/form/controls/Dropdown'
import { toast } from '@/lib/toasts/api'
import {
  ServiceAccountAccessResult,
  ServiceAccountAccessUser,
} from '@/modules/shared/query-client/useServiceAccountAdmin'

// Role kierowcy wymagają powiązania z kierowcą w bazie firmy - to zostaje na ekranie „Użytkownicy” firmy.
// Role wspólne + role konta usługowego (obowiązują we wszystkich jego firmach).
export function useRoleOptions(accountId: string): Option[] {
  const { data } = useQueryCompanyRoles(accountId)
  return React.useMemo(
    () =>
      (data?.roles ?? [])
        .filter((role) => !role.isDriver)
        .map((role) => ({ value: String(role.id), label: role.name })),
    [data],
  )
}

export const accessErrorMessage = (code: string | null | undefined): string => {
  switch (code) {
    case 'NOT_AUTHORIZED':
      return 'brak uprawnień'
    case 'USER_CANNOT_ALTER_ROLE':
      return 'nie możesz nadać tej roli (ma uprawnienia, których sam nie masz)'
    case 'COMPANY_NOT_IN_ACCOUNT':
      return 'firma nie należy do tego konta'
    case 'LOGIN_REQUIRED':
      return 'brak adresu e-mail'
    case 'NAME_REQUIRED':
      return 'osoba spoza konta - podaj imię i nazwisko'
    case 'USER_NOT_FOUND':
      return 'nie znaleziono użytkownika'
    case 'INVALID_LOGIN':
    case 'INVALID_EMAIL':
      return 'niepoprawny adres e-mail'
    case 'INVALID_ROLE':
      return 'tej roli nie można nadać w tej firmie'
    case 'PROTECTED_USER':
      return 'tego konta nie można zmieniać z poziomu konta usługowego'
    case 'USER_EMAIL_MISSING':
      return 'konto bez adresu e-mail - zmień je na ekranie „Użytkownicy” firmy'
    case 'TOO_MANY_NEW_USERS':
      return 'za dużo nowych osób naraz - zapisz w kilku partiach'
    case 'DUPLICATE_ENTRY':
      return 'powtórzony wpis'
    case 'INVALID_INPUT':
      return 'za długi e-mail, imię lub nazwisko'
    default:
      return 'nie udało się zapisać'
  }
}

export const displayName = (u: Pick<ServiceAccountAccessUser, 'firstName' | 'surname'>) =>
  [u.firstName, u.surname].filter(Boolean).join(' ')

export const rolesIn = (u: ServiceAccountAccessUser, companyId: string): number[] =>
  (u.companyRoles.find((c) => c.companyId === companyId)?.roles ?? []).map((r) => r.id)

export const sameIds = (a: number[], b: number[]) => {
  if (a.length !== b.length) return false
  const sorted = [...b].sort((x, y) => x - y)
  return [...a].sort((x, y) => x - y).every((v, i) => v === sorted[i])
}

export const hasAccountWideAccess = (u: ServiceAccountAccessUser) =>
  !!u.rosterRole || u.accountRoles.length > 0

/** Podsumowanie zapisu w toaście; zwraca wyniki z błędem. */
export function reportResults(
  results: ServiceAccountAccessResult[],
  companyName: (id: string) => string,
): ServiceAccountAccessResult[] {
  const failed = results.filter((r) => r.status === 'error')
  const ok = results.length - failed.length
  if (ok > 0) toast.success({ text: `Zapisano: ${ok}` })
  failed.slice(0, 3).forEach((r) =>
    toast.error({
      text: `${r.login} → ${companyName(r.companyId)}: ${accessErrorMessage(r.code)}`,
    }),
  )
  if (failed.length > 3) toast.error({ text: `…i ${failed.length - 3} kolejnych błędów` })
  return failed
}

// Jak pole wyszukiwania na liście kierowców.
export const inputClass =
  'tw-w-full tw-rounded tw-border tw-border-neutral-300 tw-bg-content-primary tw-px-3 tw-py-2 tw-text-sm focus:tw-border-primary focus:tw-outline-none'
